/**
 * Research Page Interactions
 * Handles scroll-based animations and enhanced interactivity
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    initScrollAnimations();
    initSmoothScroll();
    initProgressIndicator();
  }

  /**
   * Scroll-based fade-in animations using IntersectionObserver
   */
  function initScrollAnimations() {
    // Skip animations on mobile to avoid performance issues
    if (window.innerWidth <= 1024) return;

    const animatedElements = document.querySelectorAll(
      '.benefit-card, .accordion-group, .research-hero__stat'
    );

    if (animatedElements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    animatedElements.forEach((el, index) => {
      // Set initial state
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      el.style.transition = `opacity 0.6s ease ${index * 0.05}s, transform 0.6s ease ${index * 0.05}s`;
      
      observer.observe(el);
    });
  }

  /**
   * Smooth scroll for anchor links
   */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        if (href === '#' || href === '#!') return;

        const target = document.querySelector(href);
        if (!target) return;

        e.preventDefault();
        
        const headerOffset = document.querySelector('.shopify-section-header-sticky')?.offsetHeight || 80;
        const elementPosition = target.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - headerOffset - 20;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      });
    });
  }

  /**
   * Reading progress indicator (optional)
   */
  function initProgressIndicator() {
    // Create progress bar
    const progressBar = document.createElement('div');
    progressBar.className = 'research-progress-bar';
    progressBar.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 0;
      height: 3px;
      background: linear-gradient(90deg, #ea9713 0%, #d94f6e 100%);
      z-index: 9999;
      transition: width 0.1s ease;
    `;
    document.body.appendChild(progressBar);

    // Update progress on scroll
    let ticking = false;
    
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateProgress();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    // scrollHeight/clientHeight are LAYOUT READS. Reading them inside the
    // scroll rAF forced a style+layout recalc on every single scroll frame.
    // They only change on resize / content reflow, so cache them there.
    let scrollRange = 1;
    function measureRange() {
      scrollRange =
        document.documentElement.scrollHeight - document.documentElement.clientHeight || 1;
    }
    measureRange();
    window.addEventListener('resize', measureRange, { passive: true });
    window.addEventListener('load', measureRange);
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(measureRange).observe(document.documentElement);
    }

    function updateProgress() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const progress = (scrollTop / scrollRange) * 100;
      progressBar.style.width = progress + '%';
    }
  }

  /**
   * Keyboard navigation for accordions
   */
  document.addEventListener('keydown', (e) => {
    const target = e.target;
    
    // Handle Enter/Space on accordion triggers
    if ((e.key === 'Enter' || e.key === ' ') && target.hasAttribute('data-accordion-trigger')) {
      e.preventDefault();
      target.click();
    }

    // Handle Enter/Space on benefit card toggle buttons
    if ((e.key === 'Enter' || e.key === ' ') && target.hasAttribute('data-toggle-btn')) {
      e.preventDefault();
      target.click();
    }
  });

  /**
   * Lazy load images when they come into viewport
   */
  function initLazyLoading() {
    const lazyImages = document.querySelectorAll('img[loading="lazy"]');
    
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            imageObserver.unobserve(img);
          }
        });
      });

      lazyImages.forEach((img) => imageObserver.observe(img));
    }
  }

  initLazyLoading();

})();
